/**
 * Claim store (CC-Claims) — co-located with the claims module.
 *
 * Mirrors the house per-entity Zustand pattern (soStore precedent): persisted
 * filter state + pagination echo + the store-first list echo the table hook and
 * mutation hooks keep in sync. Lives under `src/components/claims/` rather than
 * `src/stores/` because the CC-Claims slice owns only `src/components/claims/**`
 * (see the slice deviation note); the contract is otherwise identical.
 *
 * Filter contract:
 * - `search`  — claim number / order number / product / owner (server-side)
 * - `side`    — 'all' | sales | purchasing (銷售索賠 / 採購索賠 dual placement)
 * - `status`  — 'all' | lifecycle status
 * - `type`    — 'all' | claim type
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { Claim, ClaimSide, ClaimStatus, ClaimType } from '@/types/api/claim';

export type ClaimSideFilter = 'all' | ClaimSide;
export type ClaimStatusFilter = 'all' | ClaimStatus;
export type ClaimTypeFilter = 'all' | ClaimType;

export interface ClaimFilters {
  search: string;
  side: ClaimSideFilter;
  status: ClaimStatusFilter;
  type: ClaimTypeFilter;
}

export interface ClaimPagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface ClaimState {
  // Data (list echo — kept in sync by useClaimsTable / mutation hooks)
  claims: Claim[];

  // Pagination echo
  pagination: ClaimPagination;

  // Filters
  filters: ClaimFilters;

  // Actions - data
  setClaims: (claims: Claim[]) => void;
  upsertClaimInList: (claim: Claim) => void;

  // Actions - pagination
  setPagination: (pagination: Partial<ClaimPagination>) => void;

  // Actions - filters
  setSearch: (search: string) => void;
  setSide: (side: ClaimSideFilter) => void;
  setStatus: (status: ClaimStatusFilter) => void;
  setType: (type: ClaimTypeFilter) => void;
  resetFilters: () => void;

  // Actions - reset
  reset: () => void;
}

const initialState = {
  claims: [] as Claim[],
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {
    search: '',
    side: 'all' as ClaimSideFilter,
    status: 'all' as ClaimStatusFilter,
    type: 'all' as ClaimTypeFilter,
  },
};

export const useClaimStore = create<ClaimState>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,

        setClaims: (claims) => set({ claims }, false, 'claim/setClaims'),

        upsertClaimInList: (claim) =>
          set(
            (state) => {
              const exists = state.claims.some((item) => item.id === claim.id);
              return {
                claims: exists
                  ? state.claims.map((item) => (item.id === claim.id ? claim : item))
                  : [claim, ...state.claims],
              };
            },
            false,
            'claim/upsertClaimInList'
          ),

        setPagination: (pagination) =>
          set(
            (state) => ({ pagination: { ...state.pagination, ...pagination } }),
            false,
            'claim/setPagination'
          ),

        setSearch: (search) =>
          set(
            (state) => ({
              filters: { ...state.filters, search },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'claim/setSearch'
          ),

        setSide: (side) =>
          set(
            (state) => ({
              filters: { ...state.filters, side },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'claim/setSide'
          ),

        setStatus: (status) =>
          set(
            (state) => ({
              filters: { ...state.filters, status },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'claim/setStatus'
          ),

        setType: (type) =>
          set(
            (state) => ({
              filters: { ...state.filters, type },
              pagination: { ...state.pagination, page: 1 },
            }),
            false,
            'claim/setType'
          ),

        resetFilters: () =>
          set(
            {
              filters: { ...initialState.filters },
              pagination: { ...initialState.pagination },
            },
            false,
            'claim/resetFilters'
          ),

        reset: () => set(initialState, false, 'claim/reset'),
      }),
      {
        name: 'claim-storage',
        partialize: (state) => ({
          filters: state.filters,
          pagination: {
            page: state.pagination.page,
            pageSize: state.pagination.pageSize,
          },
        }),
      }
    ),
    { name: 'ClaimStore' }
  )
);
